import { useEffect } from "react";
import { AdvancedMarker, InfoWindow, Map, Polyline, useMap } from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { GMAPS_KEY as KEY } from "./gmaps";

const FitBounds = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (!map || !points.length || !window.google) return undefined;
        const timer = setTimeout(() => {
            if (points.length === 1) {
                map.setCenter(points[0]);
                map.setZoom(13);
            } else {
                const b = new window.google.maps.LatLngBounds();
                points.forEach((p) => b.extend(p));
                map.fitBounds(b, 90);
            }
        }, 250);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, JSON.stringify(points)]);
    return null;
};

const GoldMarker = ({ index, active }) => (
    <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 font-mono-accent text-xs font-bold shadow-lg transition-all ${active ? "border-amber-200 bg-amber-300 text-black scale-110" : "border-amber-400 bg-[#0b0b0c] text-amber-300"}`}>
        {index + 1}
    </div>
);

const DotMarker = ({ active }) => (
    <div className={`h-3 w-3 rounded-full border-2 shadow-lg transition-all ${active ? "scale-125 border-amber-200 bg-amber-300" : "border-amber-400/90 bg-[#0b0b0c]"}`} />
);

const StayMarker = ({ active }) => (
    <div className={`flex h-7 w-7 items-center justify-center rounded-full border-2 shadow-lg transition-all ${active ? "scale-110 border-amber-200 bg-amber-300 text-black" : "border-amber-400 bg-[#151515] text-amber-300"}`}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" /><path d="M2 17h20" />
        </svg>
    </div>
);

export const MapPanel = ({ stops, extras = [], activeId = null, onMarkerClick = null, height = "440px", defaultCenter = { lat: 41.9, lng: 12.5 }, defaultZoom = 12, routePath = null }) => {
    const { t } = useLang();
    const points = stops.map((s) => ({ lat: s.lat, lng: s.lng }));
    const all = [...stops, ...extras];
    const active = all.find((s) => s.id === activeId && s.lat != null);

    if (!KEY) {
        return (
            <div data-testid="builder-map-fallback" style={{ height }} className="flex flex-col items-center justify-center rounded-2xl border border-amber-500/25 bg-[#101011] px-8 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-amber-500/40 text-amber-400"><MapPin size={24} /></span>
                <h3 className="mt-4 font-display text-xl text-amber-50">{t.builder.mapFallbackTitle}</h3>
                <p className="mt-2 max-w-md text-xs leading-relaxed text-stone-400">{t.builder.mapFallbackText}</p>
                {stops.length > 0 && (
                    <p className="mt-4 font-mono-accent text-[11px] text-amber-500/80">
                        {stops.map((s, i) => `${i + 1}. ${s.name}`).join("  ·  ")}
                    </p>
                )}
            </div>
        );
    }

    return (
        <div data-testid="builder-map" style={{ height }} className="overflow-hidden rounded-2xl border border-amber-500/25">
            <Map
                style={{ width: "100%", height: "100%" }}
                defaultCenter={defaultCenter}
                defaultZoom={defaultZoom}
                mapId="tv-itinerary-builder"
                gestureHandling="greedy"
                disableDefaultUI
                zoomControl
            >
                <FitBounds points={points.length ? points : all.map((s) => ({ lat: s.lat, lng: s.lng }))} />
                {routePath?.length > 1 && (
                    <Polyline path={routePath} strokeColor="#D6B45C" strokeOpacity={0.9} strokeWeight={3.5} geodesic clickable={false} />
                )}
                {stops.map((s, i) => (
                    <AdvancedMarker key={s.id} position={{ lat: s.lat, lng: s.lng }} onClick={() => onMarkerClick?.(s.id)}>
                        <GoldMarker index={i} active={activeId === s.id} />
                    </AdvancedMarker>
                ))}
                {extras.filter((s) => s.lat != null).map((s) => (
                    <AdvancedMarker key={s.id} position={{ lat: s.lat, lng: s.lng }} onClick={() => onMarkerClick?.(s.id)}>
                        {s.kind === "stay" ? <StayMarker active={activeId === s.id} /> : <DotMarker active={activeId === s.id} />}
                    </AdvancedMarker>
                ))}
                {active && (
                    <InfoWindow position={{ lat: active.lat, lng: active.lng }} onCloseClick={() => onMarkerClick?.(null)}>
                        <div style={{ color: "#0b0b0c", fontFamily: "Georgia, serif", fontSize: 13, padding: "2px 4px", maxWidth: 200 }}>{active.name}</div>
                    </InfoWindow>
                )}
            </Map>
        </div>
    );
};
